import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { mediaOutletService } from '@/services/mediaOutletService';
import type { CustomOutletReview } from '@/types/mediaOutlets';

interface ReviewActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actionType: 'approve' | 'reject' | 'merge' | null;
  review: CustomOutletReview | null;
  onConfirm: (data: { outletId?: string; reason?: string }) => void;
  isLoading: boolean;
}

export function ReviewActionDialog({
  open,
  onOpenChange,
  actionType,
  review,
  onConfirm,
  isLoading,
}: ReviewActionDialogProps) {
  const [selectedOutletId, setSelectedOutletId] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const { data: outlets = [] } = useQuery({
    queryKey: ['media-outlets-for-merge'],
    queryFn: () => mediaOutletService.getAllOutlets(),
    enabled: actionType === 'merge',
  });

  useEffect(() => {
    if (!open) {
      setSelectedOutletId('');
      setRejectionReason('');
    }
  }, [open, review, actionType]);

  const handleConfirm = () => {
    if (actionType === 'approve') {
      onConfirm({});
    } else if (actionType === 'reject') {
      onConfirm({ reason: rejectionReason });
    } else if (actionType === 'merge') {
      onConfirm({ outletId: selectedOutletId });
    }
  };

  const canConfirm = () => {
    if (actionType === 'approve') return true;
    if (actionType === 'reject') return rejectionReason.trim().length > 0;
    if (actionType === 'merge') return selectedOutletId.length > 0;
    return false;
  };

  if (!review) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {actionType === 'approve' && 'Approve Custom Outlet'}
            {actionType === 'reject' && 'Reject Custom Outlet'}
            {actionType === 'merge' && 'Merge with Existing Outlet'}
          </DialogTitle>
          <DialogDescription>
            {actionType === 'approve' &&
              'This will create a new media outlet from this custom submission.'}
            {actionType === 'reject' &&
              'This will reject the custom outlet submission. Please provide a reason.'}
            {actionType === 'merge' &&
              'This will merge this custom outlet with an existing outlet in the database.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Custom Outlet Name</Label>
            <div className="text-sm font-medium">{review.custom_outlet_name}</div>
          </div>


          <div className="space-y-2">
            <Label>Total Submissions</Label>
            <div className="text-sm text-muted-foreground">
              {review.submission_count} time(s)
            </div>
          </div>

          {actionType === 'merge' && (
            <div className="space-y-2">
              <Label htmlFor="merge-outlet">Select Existing Outlet</Label>
              <Select value={selectedOutletId} onValueChange={setSelectedOutletId}>
                <SelectTrigger id="merge-outlet">
                  <SelectValue placeholder="Choose an outlet..." />
                </SelectTrigger>
                <SelectContent>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.outlet_name} ({outlet.outlet_category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {actionType === 'reject' && (
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Rejection Reason</Label>
              <Textarea
                id="rejection-reason"
                placeholder="Explain why this submission is being rejected..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm() || isLoading}
          >
            {isLoading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
